/**
 * Interceptor global de respostas do axios.
 *
 * Detecta 401 (token ausente / expirado / inválido) em QUALQUER request
 * da aplicação e dispara um único evento `auth:logout` no `window`. O
 * listener registrado em `App.js` cuida de limpar a sessão e redirecionar
 * para a tela de login.
 *
 * Por que um evento de janela em vez de chamar `logout()` direto aqui?
 *  - Evita import circular entre este módulo e `frontend/js/auth.js`.
 *  - Permite que o listener seja registrado uma única vez, no topo da
 *    árvore React, e tenha acesso ao `useNavigate`.
 *  - Garante disparo único mesmo se várias requests falharem em paralelo
 *    (usamos um flag interno `__auth401Dispatched`).
 *
 * Idempotente: importar este módulo várias vezes só registra o
 * interceptor uma vez.
 */
import axios from "axios";
import { API_BASE_URL } from "./api.js";

const FLAG_KEY = "__auth401Dispatched";
const EVENT_NAME = "auth:logout";

function isApiRequest(error) {
  const url = error?.config?.url || "";
  // Intercepta só chamadas para o nosso backend (evita disparar com 401
  // de CDNs, auth0, etc).
  if (url.startsWith(API_BASE_URL)) return true;
  // Chamadas relativas (sem host) também contam como API local.
  if (/^\/(?!\/)/.test(url)) return true;
  return false;
}

function dispatchAuthLogout(reason) {
  if (typeof window === "undefined") return;
  if (window[FLAG_KEY]) return;
  window[FLAG_KEY] = true;

  // Remove credenciais de autenticação sem destruir outros dados locais ou preferências
  try {
    const chavesSessao = [
      "token",
      "user",
      "usuario",
      "permissoes",
      "auth",
      "currentUser",
      "estudante_id",
      "estudante_tce_id",
      "estudante_tca_id",
      "cpf_estudante",
      "estudante_cpf",
    ];
    chavesSessao.forEach((k) => {
      window.localStorage.removeItem(k);
      window.sessionStorage.removeItem(k);
    });
  } catch {
    /* storage indisponível */
  }

  // Detalhe para diagnóstico no console.
  if (typeof console !== "undefined") {
    console.warn(
      "[axiosInterceptors] 401 detectado — sessão será encerrada. Motivo:",
      reason,
    );
  }

  // Dispara o evento. O listener em App.js faz navigate("/").
  try {
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, { detail: { reason } }),
    );
  } catch {
    /* CustomEvent indisponível: fallback para location */
    window.location.replace("/");
  }

  // Libera o flag após 2s para permitir novo login + nova sessão sem
  // ter que recarregar o módulo.
  setTimeout(() => {
    window[FLAG_KEY] = false;
  }, 2000);
}

let installed = false;

export function instalarInterceptor401() {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      if (status === 401 && isApiRequest(error)) {
        const cfg = error.config || {};
        const url = cfg.url || "(sem url)";
        const method = (cfg.method || "get").toUpperCase();
        dispatchAuthLogout(`401 em ${method} ${url}`);
      }
      return Promise.reject(error);
    },
  );
}

export { EVENT_NAME as AUTH_LOGOUT_EVENT };

// Auto-instala ao ser importado. App.js importa este módulo para
// garantir que o interceptor esteja ativo antes de qualquer página
// montar.
instalarInterceptor401();
